import { Bars3Icon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import { memo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SourcesSidebar } from "../features/sources";
import { ThreadsSidebar } from "../features/threads";
import { WorkflowsSidebar } from "../features/workflows";
import IconButton from "../ui/buttons/IconButton";
import ResizeHandle from "../ui/layout/ResizeHandle";
import { useStoredSize } from "../ui/layout/useStoredSize";
import { classNames } from "../ui/styles/classNames";
import type { SidebarSectionId } from "./appSections";

/**
 * Every sidebar's one width (UX-3), in pixels: 240 until the user drags its edge (F138), then
 * theirs, for every section and pane. It never takes more than half its pane.
 */
const SIDEBAR_WIDTH = { initial: 240, min: 200, max: 640 } as const;
const SIDEBAR_WIDTH_STEP = 16;
const SIDEBAR_WIDTH_STORAGE_KEY = "dmw.sidebarWidth";

interface SidebarProps {
  open: boolean;
  section: SidebarSectionId;
  onOpenChange: (open: boolean) => void;
}

/**
 * A tab's sidebar, at the left of the tab's pane (WA-10): its content follows the tab's section
 * (UX review §4.2), so two panes side by side show two sidebars. Closed, a narrow rail with a
 * button opens it again. Its right edge resizes it (F138).
 */
export default function Sidebar({ open, section, onOpenChange }: SidebarProps) {
  const { t } = useTranslation();
  const aside = useRef<HTMLElement>(null);
  const [width, setStoredWidth] = useStoredSize(SIDEBAR_WIDTH_STORAGE_KEY, SIDEBAR_WIDTH);
  const widthAt = useCallback((clientX: number) => clientX - (aside.current?.getBoundingClientRect().left ?? 0), []);
  // Never more than half its pane, also as stored, so the handle holds the width it shows (Sol S1);
  // CSS keeps the half when the window narrows later. A pane not laid out yet sets no limit.
  const setWidth = useCallback((next: number) => {
    const paneWidth = aside.current?.parentElement?.getBoundingClientRect().width ?? 0;
    const half = paneWidth > 0 ? Math.floor(paneWidth / 2) : SIDEBAR_WIDTH.max;
    setStoredWidth(Math.max(SIDEBAR_WIDTH.min, Math.min(next, half)));
  }, [setStoredWidth]);

  // Closed, the content stays mounted, so a list keeps its place and filter.
  return (
    <>
      {open ? null : (
        <div className="app-sidebar flex shrink-0 flex-col items-center border-r py-2">
          <IconButton onClick={() => { onOpenChange(true); }} label={t("shell.openSidebar")} className="h-8 w-8">
            <Bars3Icon className="h-5 w-5" />
          </IconButton>
        </div>
      )}
      <aside
        ref={aside}
        className={classNames("app-sidebar relative flex max-w-[50%] shrink-0 flex-col border-r", !open && "hidden")}
        style={{ width: `${String(width)}px` }}
        aria-label={t("shell.sidebar")}
        aria-hidden={!open}
        inert={!open}
      >
        <div className="app-surface flex h-12 shrink-0 items-center justify-between border-b px-3">
          <p className="app-title truncate text-sm font-semibold">{t(`sections.${section}`)}</p>
          <IconButton onClick={() => { onOpenChange(false); }} label={t("shell.closeSidebar")} className="h-8 w-8 shrink-0">
            <ChevronLeftIcon className="h-5 w-5" />
          </IconButton>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <SectionSidebar section={section} />
        </div>
        <ResizeHandle
          label={t("shell.resizeSidebar")}
          value={width}
          min={SIDEBAR_WIDTH.min}
          max={SIDEBAR_WIDTH.max}
          step={SIDEBAR_WIDTH_STEP}
          valueAt={widthAt}
          onChange={setWidth}
          look="edge"
          className="absolute inset-y-0 -right-[3px] z-10 w-[5px]"
        />
      </aside>
    </>
  );
}

/** The section's own sidebar; it doesn't render again while the frame's width changes (F138). */
const SectionSidebar = memo(function SectionSidebar({ section }: { section: SidebarSectionId }) {
  switch (section) {
    case "sources":
      return <SourcesSidebar />;
    case "threads":
      return <ThreadsSidebar />;
    case "workflows":
      return <WorkflowsSidebar />;
  }
});
